import { useMemo } from "react";
import { getActiveHoliday } from "./holiday-trigger";
import type { TalentPopTrigger } from "./notification-copy";
import { useTalentPopVolumeTrigger } from "./use-volume-trigger";

/**
 * Composes the two TalentPop triggers — month-to-date volume surge and
 * holiday run-up — into a single "is the feature active right now" signal.
 *
 * The volume trigger wins when both fire on the same day: a real surge is
 * the more urgent prompt and the notification copy ("Ticket volume spike
 * detected") more accurately describes why the merchant is seeing the
 * button.
 *
 * Returns `null` when neither trigger is firing. Drives both the
 * `TalentPopButton` visibility and the TalentPop notification copy via
 * `getTalentPopNotificationCopy`. The returned object is memoized on its
 * fields so consumers can safely use it as a `useEffect` dependency.
 */
export function useTalentPopTrigger(): TalentPopTrigger | null {
  const volumeSurge = useTalentPopVolumeTrigger();
  const holidayKey = getActiveHoliday(Temporal.Now.plainDateISO());
  return useMemo(() => {
    if (volumeSurge) {
      return { type: "volume" };
    }
    if (holidayKey) {
      return { type: "holiday", holidayKey };
    }
    return null;
  }, [volumeSurge, holidayKey]);
}
